import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Dialog,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { QRCode } from 'react-qrcode-logo';
import { useSnackbar } from 'notistack';
import useSWR from 'swr';
import { Event, useEventsApi } from 'services/api';
import { CHECK_IN_COLORS } from './colors';
import { formatEventDateLabel, formatEventTimeRange } from './format';

interface EventCodeTabProps {
  event: Event;
}

const QR_CANVAS_ID = 'event-check-in-qr';

const windowLabel = (state?: string): { text: string; color: string } => {
  if (state === 'open') return { text: 'Open', color: CHECK_IN_COLORS.green };
  if (state === 'not_started')
    return { text: 'Not Started', color: CHECK_IN_COLORS.navy };
  if (state === 'closed')
    return { text: 'Closed', color: CHECK_IN_COLORS.red };
  return { text: 'Check-in status unavailable', color: CHECK_IN_COLORS.textSecondary };
};

const windowSettingLabel = (
  override?: 'auto' | 'open' | 'closed',
): string => {
  if (override === 'open') return 'Open window';
  if (override === 'closed') return 'Closed window';
  return 'Auto window';
};

const TertiaryButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  labelColor: string;
  onClick: () => void;
}> = ({ icon, label, labelColor, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      backgroundColor: CHECK_IN_COLORS.white,
      border: `1px solid ${CHECK_IN_COLORS.border}`,
      borderRadius: '8px',
      minHeight: '44px',
      height: '44px',
      px: '14px',
      py: '7px',
      gap: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {icon}
    <Typography
      sx={{
        fontFamily: 'Poppins, sans-serif',
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        color: labelColor,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </ButtonBase>
);

const OptionToggle: React.FC<{
  options: readonly { id: string; label: string }[];
  activeId: string;
  disabled?: boolean;
  onSelect: (id: string) => void;
}> = ({ options, activeId, disabled, onSelect }) => (
  <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
    {options.map(option => {
      const active = option.id === activeId;
      return (
        <ButtonBase
          key={option.id}
          disabled={disabled}
          onClick={() => onSelect(option.id)}
          sx={{
            flex: 1,
            height: '36px',
            borderRadius: '8px',
            border: `1px solid ${
              active ? CHECK_IN_COLORS.navy : CHECK_IN_COLORS.border
            }`,
            backgroundColor: active
              ? 'rgba(35,48,95,0.04)'
              : CHECK_IN_COLORS.white,
            fontFamily: 'Poppins, sans-serif',
            fontSize: '13px',
            fontWeight: active ? 600 : 400,
            color: CHECK_IN_COLORS.navy,
          }}
        >
          {option.label}
        </ButtonBase>
      );
    })}
  </Box>
);

export const EventCodeTab: React.FC<EventCodeTabProps> = ({ event }) => {
  const eventsApi = useEventsApi();
  const { enqueueSnackbar } = useSnackbar();
  const [qrValue, setQrValue] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [view, setView] = useState<'code' | 'settings'>('code');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && event?.id) {
      setQrValue(`${window.location.origin}/events/${event.id}/check-in`);
    }
  }, [event?.id]);

  const { data: access, mutate } = useSWR(
    event?.id ? `/events/${event.id}/check-in-access` : null,
    async () => {
      const result = await eventsApi.getCheckInAccess(event.id);
      return result.success ? result.value : undefined;
    },
    {
      refreshInterval: 15000,
      revalidateOnFocus: false,
    },
  );

  const settingsSubtext = useMemo(() => {
    const windowText = windowSettingLabel(access?.checkInStateOverride);
    const walkText = access?.allowWalkInCheckIn
      ? 'Walk-ins allowed'
      : 'RSVP required';
    return `${windowText} · ${walkText}`;
  }, [access?.checkInStateOverride, access?.allowWalkInCheckIn]);

  const handleDownload = () => {
    const qrCanvas = document.getElementById(
      QR_CANVAS_ID,
    ) as HTMLCanvasElement | null;
    if (!qrCanvas) return;

    const canvas = document.createElement('canvas');
    const width = 720;
    const height = 900;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#141F33';
    ctx.font = '600 28px Poppins, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(event.name, width / 2, 72, width - 80);
    ctx.font = '500 20px Poppins, sans-serif';
    ctx.fillText('Scan to Check In', width / 2, 116);
    ctx.drawImage(qrCanvas, (width - 420) / 2, 160, 420, 420);
    ctx.font = '400 16px Poppins, sans-serif';
    ctx.fillStyle = '#383838';
    ctx.fillText(formatEventDateLabel(event.date), width / 2, 640);
    ctx.fillText(
      formatEventTimeRange(event.startTime, event.endTime),
      width / 2,
      668,
    );

    const link = document.createElement('a');
    link.download = `${event.name.replace(/[^\w]+/g, '-').toLowerCase()}-check-in.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const updateSettings = async (patch: {
    allowWalkInCheckIn?: boolean;
    checkInStateOverride?: 'auto' | 'open' | 'closed';
  }) => {
    setSaving(true);
    const result = await eventsApi.updateCheckInSettings(event.id, patch);
    setSaving(false);
    if (result.success) {
      await mutate(result.value, false);
    } else {
      enqueueSnackbar('Could not update check-in settings', {
        variant: 'error',
      });
    }
  };

  const rootSx = {
    flex: 1,
    minHeight: 0,
    backgroundColor: CHECK_IN_COLORS.surfaceSubtle,
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    p: '20px',
    overflow: 'auto',
  } as const;

  if (view === 'settings') {
    return (
      <Box sx={rootSx}>
        <ButtonBase
          onClick={() => setView('code')}
          sx={{
            alignSelf: 'flex-start',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            ml: '-4px',
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 22, color: CHECK_IN_COLORS.text }} />
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px',
              color: CHECK_IN_COLORS.text,
            }}
          >
            Check-in settings
          </Typography>
        </ButtonBase>

        <Box
          sx={{
            width: '100%',
            backgroundColor: CHECK_IN_COLORS.white,
            border: `1px solid ${CHECK_IN_COLORS.border}`,
            borderRadius: '8px',
            p: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                color: CHECK_IN_COLORS.text,
              }}
            >
              Walk-in check-in
            </Typography>
            <OptionToggle
              options={[
                { id: 'off', label: 'RSVP only' },
                { id: 'on', label: 'Allowed' },
              ]}
              activeId={access?.allowWalkInCheckIn ? 'on' : 'off'}
              disabled={saving}
              onSelect={id =>
                void updateSettings({ allowWalkInCheckIn: id === 'on' })
              }
            />
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '18px',
                color: CHECK_IN_COLORS.textSecondary,
              }}
            >
              When allowed, any student can check in by scanning the event QR
              code.
            </Typography>
          </Box>

          <Box
            sx={{
              height: '1px',
              width: '100%',
              backgroundColor: CHECK_IN_COLORS.border,
            }}
          />

          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                color: CHECK_IN_COLORS.text,
              }}
            >
              Check-in window
            </Typography>
            <OptionToggle
              options={[
                { id: 'auto', label: 'Auto' },
                { id: 'open', label: 'Open' },
                { id: 'closed', label: 'Closed' },
              ]}
              activeId={access?.checkInStateOverride ?? 'auto'}
              disabled={saving}
              onSelect={id =>
                void updateSettings({
                  checkInStateOverride: id as 'auto' | 'open' | 'closed',
                })
              }
            />
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '18px',
                color: CHECK_IN_COLORS.textSecondary,
              }}
            >
              Auto opens check-in around the event start time. Override to force
              it open or closed.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={rootSx}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
          alignItems: 'center',
          textAlign: 'center',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '26px',
            color: CHECK_IN_COLORS.text,
            width: '100%',
          }}
        >
          {event.name}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '18px',
            color: CHECK_IN_COLORS.textSecondary,
            width: '100%',
          }}
        >
          {formatEventDateLabel(event.date)}
          {formatEventTimeRange(event.startTime, event.endTime)
            ? ` · ${formatEventTimeRange(event.startTime, event.endTime)}`
            : ''}
        </Typography>
      </Box>

      <Box
        sx={{
          height: '1px',
          width: '100%',
          backgroundColor: CHECK_IN_COLORS.border,
        }}
      />

      <Box
        sx={{
          width: '100%',
          backgroundColor: CHECK_IN_COLORS.border,
          borderRadius: '8px',
          px: '14px',
          py: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            color: windowLabel(access?.checkInWindowState).color,
            textAlign: 'center',
          }}
        >
          {windowLabel(access?.checkInWindowState).text}
        </Typography>
      </Box>

      <Box
        sx={{
          width: '100%',
          backgroundColor: CHECK_IN_COLORS.white,
          border: `1px solid ${CHECK_IN_COLORS.border}`,
          borderRadius: '8px',
          px: '16px',
          py: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '18px',
            color: CHECK_IN_COLORS.textSecondary,
            textAlign: 'center',
            width: '100%',
          }}
        >
          Attendees scan this to check in
        </Typography>
        <Box
          sx={{
            width: 250,
            height: 250,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: CHECK_IN_COLORS.white,
          }}
        >
          {qrValue && (
            <QRCode
              id={QR_CANVAS_ID}
              value={qrValue}
              size={250}
              quietZone={0}
            />
          )}
        </Box>
        <Box
          sx={{
            height: '1px',
            width: '100%',
            backgroundColor: CHECK_IN_COLORS.border,
          }}
        />
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <TertiaryButton
            icon={
              <DownloadIcon
                sx={{ fontSize: 16, color: CHECK_IN_COLORS.text }}
              />
            }
            label="Download"
            labelColor={CHECK_IN_COLORS.text}
            onClick={handleDownload}
          />
          <TertiaryButton
            icon={
              <FullscreenIcon
                sx={{ fontSize: 16, color: CHECK_IN_COLORS.navyLink }}
              />
            }
            label="Display full screen"
            labelColor={CHECK_IN_COLORS.navyLink}
            onClick={() => setFullscreen(true)}
          />
        </Box>
      </Box>

      <ButtonBase
        onClick={() => setView('settings')}
        sx={{
          width: '100%',
          backgroundColor: CHECK_IN_COLORS.white,
          border: `1px solid ${CHECK_IN_COLORS.border}`,
          borderRadius: '8px',
          px: '16px',
          py: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            flex: 1,
            minWidth: 0,
            alignItems: 'flex-start',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '24px',
              color: CHECK_IN_COLORS.text,
            }}
          >
            Check-in settings
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '18px',
              color: CHECK_IN_COLORS.textSecondary,
            }}
          >
            {settingsSubtext}
          </Typography>
        </Box>
        <ChevronRightIcon
          sx={{ fontSize: 20, color: CHECK_IN_COLORS.textSecondary }}
        />
      </ButtonBase>

      <Dialog open={fullscreen} onClose={() => setFullscreen(false)} fullScreen>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: CHECK_IN_COLORS.white,
            px: '24px',
            textAlign: 'center',
          }}
        >
          <IconButton
            onClick={() => setFullscreen(false)}
            aria-label="Close"
            sx={{ position: 'absolute', top: 16, left: 16 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              fontSize: '28px',
              mb: '8px',
            }}
          >
            {event.name}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '16px',
              mb: '24px',
            }}
          >
            Scan to Check In
          </Typography>
          {qrValue && <QRCode value={qrValue} size={320} quietZone={8} />}
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '16px',
              mt: '24px',
            }}
          >
            {formatEventDateLabel(event.date)}
          </Typography>
          <Typography
            sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px' }}
          >
            {formatEventTimeRange(event.startTime, event.endTime)}
          </Typography>
        </Box>
      </Dialog>
    </Box>
  );
};
